import { useAuth, useLocalStorage } from "@elit-hair/elit-tools";
import { Spinner } from "@elit-hair/elit-ui";
import { createFileRoute } from "@tanstack/react-router";
import { AnimatePresence, motion } from "framer-motion";
import { DateTime } from "luxon";
import { useCallback, useMemo } from "react";
import { TbFileDescription } from "react-icons/tb";
import z from "zod";

import { LOCAL_STORAGE_KEYS } from "@/constants/local-storage-keys";
import { useTeams } from "@/hooks/queries/team/use-team-queries";
import { AgentPerformanceDashboard } from "@/pages/home/components/agent-performance-dashboard";
import { AgentPerformanceDashboardSearchSchema } from "@/pages/home/components/agent-performance-dashboard/agent-performance-dashboard-search.schema";
import { useDashboardRole } from "@/pages/home/components/role-dashboard/hooks/use-dashboard-role";
import { fadeInUp, transitions } from "@/utils/animations";
import { APP_ROUTES_FULL, APP_ROUTES_TO } from "@/utils/routes";
import { searchValidator } from "@/utils/search-validator";

import { PerformanceCalendarCapacitiesPage } from "./calendar-capacities";
import { CallVolumeReport } from "./call-volume";
import { ReachabilityAnalysisReport } from "./reachability-analysis";
import { PrpReportsPanel } from "./reports/components/prp-reports-panel";
import { ReportTypeSelect } from "./reports/components/report-type-select";
import { SalesSummariesPanel } from "./reports/components/sales-summaries-panel";
import { REPORT_DASHBOARD_HEADER_CLASS, REPORT_DASHBOARD_SHELL_CLASS } from "./reports/constants/report-ui";
import {
    REPORT_DASHBOARD_IDS,
    REPORT_DASHBOARD_OPTIONS,
    REPORT_MAIN_CATEGORY_IDS,
    REPORT_VIEW_IDS,
    type ReportDashboardId,
    type ReportViewId,
    SALES_SUMMARY_PERIOD_IDS,
    type SalesSummaryPeriodId,
} from "./reports/types";
import { findPrpTeam, getPrpTeamAgents } from "./reports/utils/prp-team-members";
import { REPORTS_PAGE_PERMISSIONS, canViewReportDashboard } from "./reports/utils/report-access";
import {
    getReportDashboardOption,
    getReportDashboardsForIds,
    isReportDashboardId,
    resolveReportDashboard,
    resolveSalesPeriod,
} from "./reports/utils/report-selection";
import { SettingsPriorityReport } from "./setting-priority-report";

const ReportsSearchSchema = AgentPerformanceDashboardSearchSchema.extend({
    category: z
        .enum([
            REPORT_MAIN_CATEGORY_IDS.PRP,
            REPORT_MAIN_CATEGORY_IDS.SALES_SUMMARIES,
            REPORT_MAIN_CATEGORY_IDS.SETTING_CAPACITIES,
            REPORT_MAIN_CATEGORY_IDS.CALENDAR_CAPACITIES,
            REPORT_MAIN_CATEGORY_IDS.TEAM_PERFORMANCE,
            REPORT_MAIN_CATEGORY_IDS.CALL_VOLUME,
            REPORT_MAIN_CATEGORY_IDS.REACHABILITY_ANALYSIS,
        ])
        .optional(),
    period: z
        .enum([SALES_SUMMARY_PERIOD_IDS.DAILY, SALES_SUMMARY_PERIOD_IDS.WEEKLY, SALES_SUMMARY_PERIOD_IDS.MONTHLY])
        .optional(),
    agent: z.string().optional(),
    dailyDate: z.string().optional(),
    weeklyDate: z.string().optional(),
    month: z.string().optional(),
    prpDate: z.string().optional(),
    prpView: z.string().optional(),
    callPurpose: z.string().optional(),
    date: z.string().optional(),
});

export type ReportsSearch = z.infer<typeof ReportsSearchSchema>;

function isReportViewId(value: string): value is ReportViewId {
    const reportTabs: ReportViewId[] = [
        REPORT_VIEW_IDS.FIRST_HALF,
        REPORT_VIEW_IDS.SECOND_HALF,
        REPORT_VIEW_IDS.DAILY,
        REPORT_VIEW_IDS.WEEKLY,
    ];
    return reportTabs.some((tab) => tab === value);
}

export const Route = createFileRoute(APP_ROUTES_FULL.PERFORMANCE_REPORTS)({
    component: PerformanceReportsPage,
    validateSearch: searchValidator(ReportsSearchSchema),
    staticData: {
        config: {
            breadcrumb: {
                title: "reports",
                to: APP_ROUTES_TO.PERFORMANCE_REPORTS,
            },
            sidebar: {
                label: "reports",
                icon: TbFileDescription,
            },
            title: "reports",
        },
        permissions: REPORTS_PAGE_PERMISSIONS,
    },
});

function PerformanceReportsPage() {
    const { userData } = useAuth();
    const { permissionCodes: previewPermissionCodes, isPreview } = useDashboardRole();
    const searchParams = Route.useSearch();
    const navigate = Route.useNavigate();
    const { data: teamsResponse, isLoading: isLoadingTeams } = useTeams();

    const [lastSelectedAgentId] = useLocalStorage<string | null>(
        LOCAL_STORAGE_KEYS.SELECTED_AGENT_REPORTS.key,
        LOCAL_STORAGE_KEYS.SELECTED_AGENT_REPORTS.default
    );

    const selectedSalesAgentId = searchParams.agent ?? lastSelectedAgentId ?? undefined;
    const permissionCodes = isPreview ? previewPermissionCodes : (userData?.permissionCodes ?? []);

    const teams = teamsResponse?.data ?? [];
    const prpTeam = useMemo(() => findPrpTeam(teams), [teams]);
    const prpTeamAgents = useMemo(() => getPrpTeamAgents(teams), [teams]);

    const availableDashboards = useMemo(
        (): ReportDashboardId[] =>
            REPORT_DASHBOARD_OPTIONS.map((option) => option.id).filter((id) =>
                canViewReportDashboard(id, permissionCodes)
            ),
        [permissionCodes]
    );

    const dashboardOptions = useMemo(() => getReportDashboardsForIds(availableDashboards), [availableDashboards]);

    const selectedDashboard = useMemo(
        () => resolveReportDashboard(searchParams.category, availableDashboards),
        [searchParams.category, availableDashboards]
    );

    const salesPeriod = useMemo(() => resolveSalesPeriod(searchParams.period), [searchParams.period]);

    const prpReportDate = useMemo(() => {
        if (!searchParams.prpDate) {
            return undefined;
        }
        const parsed = DateTime.fromISO(searchParams.prpDate);
        return parsed.isValid ? parsed : undefined;
    }, [searchParams.prpDate]);

    const prpReportView = useMemo(() => {
        if (searchParams.prpView && typeof searchParams.prpView === "string" && isReportViewId(searchParams.prpView)) {
            return searchParams.prpView;
        }
        return REPORT_VIEW_IDS.FIRST_HALF;
    }, [searchParams.prpView]);

    const salesDailyDate = useMemo(() => {
        if (!searchParams.dailyDate) {
            return undefined;
        }
        const parsed = DateTime.fromISO(searchParams.dailyDate);
        return parsed.isValid ? parsed : undefined;
    }, [searchParams.dailyDate]);

    const salesWeeklyDate = useMemo(() => {
        if (!searchParams.weeklyDate) {
            return undefined;
        }
        const parsed = DateTime.fromISO(searchParams.weeklyDate);
        return parsed.isValid ? parsed : undefined;
    }, [searchParams.weeklyDate]);

    const salesMonth = useMemo(() => {
        if (!searchParams.month) {
            return undefined;
        }
        const parsed = DateTime.fromISO(searchParams.month);
        return parsed.isValid ? parsed : undefined;
    }, [searchParams.month]);

    const buildDachSearch = useCallback(
        (overrides: Record<string, unknown> = {}) => {
            const searchObj: Record<string, unknown> = {
                category: REPORT_MAIN_CATEGORY_IDS.SALES_SUMMARIES,
                period: salesPeriod,
                ...overrides,
            };

            if (searchParams.agent && typeof searchParams.agent === "string") {
                searchObj.agent = searchParams.agent;
            } else if (selectedSalesAgentId) {
                searchObj.agent = selectedSalesAgentId;
            }

            if (salesDailyDate && searchObj.dailyDate === undefined) {
                searchObj.dailyDate = salesDailyDate.toISODate();
            }
            if (salesWeeklyDate && searchObj.weeklyDate === undefined) {
                searchObj.weeklyDate = salesWeeklyDate.toISODate();
            }
            if (salesMonth && searchObj.month === undefined) {
                searchObj.month = salesMonth.toISODate();
            }

            return searchObj;
        },
        [salesPeriod, searchParams.agent, selectedSalesAgentId, salesDailyDate, salesWeeklyDate, salesMonth]
    );

    const buildPrpSearch = useCallback(
        (overrides: Record<string, unknown> = {}) => {
            const searchObj: Record<string, unknown> = {
                category: REPORT_MAIN_CATEGORY_IDS.PRP,
                prpView: prpReportView,
                ...overrides,
            };

            if (prpReportDate && searchObj.prpDate === undefined) {
                searchObj.prpDate = prpReportDate.toISODate();
            }

            return searchObj;
        },
        [prpReportDate, prpReportView]
    );

    const handleDashboardChange = useCallback(
        (dashboardId: ReportDashboardId) => {
            if (!isReportDashboardId(dashboardId)) {
                return;
            }

            const option = getReportDashboardOption(dashboardId);
            const search =
                option.id === REPORT_DASHBOARD_IDS.SALES_PRP
                    ? buildPrpSearch()
                    : option.id === REPORT_DASHBOARD_IDS.SALES_DACH
                      ? buildDachSearch({ category: option.category })
                      : { category: option.category };

            navigate({
                to: Route.to,
                search,
                replace: true,
            });
        },
        [navigate, buildDachSearch, buildPrpSearch]
    );

    const handlePrpDateChange = useCallback(
        (date: DateTime) => {
            navigate({
                to: Route.to,
                search: buildPrpSearch({
                    prpDate: date.toISODate(),
                }),
                replace: true,
            });
        },
        [navigate, buildPrpSearch]
    );

    const handlePrpViewChange = useCallback(
        (view: ReportViewId) => {
            navigate({
                to: Route.to,
                search: buildPrpSearch({
                    prpView: view,
                }),
                replace: true,
            });
        },
        [navigate, buildPrpSearch]
    );

    const handleSalesPeriodChange = useCallback(
        (period: SalesSummaryPeriodId) => {
            navigate({
                to: Route.to,
                search: buildDachSearch({ period }),
                replace: true,
            });
        },
        [navigate, buildDachSearch]
    );

    const handleSalesDailyDateChange = useCallback(
        (date: DateTime) => {
            navigate({
                to: Route.to,
                search: buildDachSearch({
                    dailyDate: date.toISODate(),
                }),
                replace: true,
            });
        },
        [navigate, buildDachSearch]
    );

    const handleSalesWeeklyDateChange = useCallback(
        (date: DateTime) => {
            navigate({
                to: Route.to,
                search: buildDachSearch({
                    weeklyDate: date.toISODate(),
                }),
                replace: true,
            });
        },
        [navigate, buildDachSearch]
    );

    const handleSalesMonthChange = useCallback(
        (month: DateTime) => {
            navigate({
                to: Route.to,
                search: buildDachSearch({
                    month: month.toISODate(),
                }),
                replace: true,
            });
        },
        [navigate, buildDachSearch]
    );

    const showSelectedDashboard = availableDashboards.includes(selectedDashboard.id);
    const showDachPanel = showSelectedDashboard && selectedDashboard.id === REPORT_DASHBOARD_IDS.SALES_DACH;
    const showPrpPanel =
        showSelectedDashboard && selectedDashboard.id === REPORT_DASHBOARD_IDS.SALES_PRP && Boolean(prpTeam);
    return (
        <div className="mx-auto flex w-full flex-col px-1 py-1 sm:px-2 sm:py-2">
            <div className={REPORT_DASHBOARD_SHELL_CLASS}>
                {dashboardOptions.length > 0 ? (
                    <div className={REPORT_DASHBOARD_HEADER_CLASS}>
                        <ReportTypeSelect
                            value={selectedDashboard.id}
                            options={dashboardOptions}
                            onValueChange={handleDashboardChange}
                        />
                    </div>
                ) : null}

                <AnimatePresence mode="wait">
                    <motion.div
                        key={selectedDashboard.id}
                        {...fadeInUp}
                        transition={transitions.smooth}
                        className="flex min-h-0 flex-col"
                    >
                        {showSelectedDashboard &&
                        selectedDashboard.id === REPORT_DASHBOARD_IDS.SALES_PRP &&
                        isLoadingTeams ? (
                            <div className="flex min-h-[50vh] items-center justify-center">
                                <Spinner />
                            </div>
                        ) : null}
                        {showPrpPanel ? (
                            <PrpReportsPanel
                                prpTeamAgents={prpTeamAgents}
                                selectedReportDate={prpReportDate ?? DateTime.now().startOf("day")}
                                selectedReportView={prpReportView}
                                onReportDateChange={handlePrpDateChange}
                                onReportViewChange={handlePrpViewChange}
                            />
                        ) : null}
                        {showDachPanel ? (
                            <SalesSummariesPanel
                                activePeriod={salesPeriod}
                                selectedAgentId={selectedSalesAgentId}
                                selectedDailyDate={salesDailyDate}
                                selectedWeeklyDate={salesWeeklyDate}
                                selectedMonth={salesMonth}
                                onPeriodChange={handleSalesPeriodChange}
                                onDailyDateChange={handleSalesDailyDateChange}
                                onWeeklyDateChange={handleSalesWeeklyDateChange}
                                onMonthChange={handleSalesMonthChange}
                            />
                        ) : null}
                        {showSelectedDashboard && selectedDashboard.id === REPORT_DASHBOARD_IDS.SETTING_CAPACITIES ? (
                            <SettingsPriorityReport />
                        ) : null}
                        {showSelectedDashboard && selectedDashboard.id === REPORT_DASHBOARD_IDS.CALENDAR_CAPACITIES ? (
                            <PerformanceCalendarCapacitiesPage />
                        ) : null}
                        {showSelectedDashboard && selectedDashboard.id === REPORT_DASHBOARD_IDS.TEAM_PERFORMANCE ? (
                            <AgentPerformanceDashboard />
                        ) : null}
                        {showSelectedDashboard && selectedDashboard.id === REPORT_DASHBOARD_IDS.CALL_VOLUME ? (
                            <CallVolumeReport />
                        ) : null}
                        {showSelectedDashboard &&
                        selectedDashboard.id === REPORT_DASHBOARD_IDS.REACHABILITY_ANALYSIS ? (
                            <ReachabilityAnalysisReport />
                        ) : null}
                    </motion.div>
                </AnimatePresence>
            </div>
        </div>
    );
}
